/**
 * Copyright © 2026 Adnuntius AS.
 * UI-Router state declarations.
 */
import {Ng2StateDeclaration} from '@uirouter/angular';
import {firstValueFrom, tap} from 'rxjs';

import {PERMISSIONS} from '../../components/auth/auth-constants';
import {UserProfileSaverService} from '../../components/session/user-profile-saver.service';
import {SignalAdminResourceContextActionsComponent} from '../common/controller/signal-admin-resource-context-actions.component';
import {SignalAdminResourcePageState} from '../common/controller/signal-admin-resource-page-state';
import {SignalAdminResourceTitleComponent} from '../common/controller/signal-admin-resource-title.component';
import {ListDefaultActionsComponent} from '../common/directives/list-default-actions.component';
import {stateTitleView} from '../common/directives/html/state-title.component';
import {AdnRouterHelperApi, StateDeclarationBuilder} from '../routing/state-utils';
import {ReportTemplateApiService, ReportTemplateModel} from './report-template-api.service';
import {ReportTemplateCopyModalComponent} from './report-template-copy-modal.component';
import {ReportTemplateEditPageComponent} from './report-template-edit-page.component';
import {ReportTemplateRoutePageComponent} from './report-template-route-page.component';

export function reportTemplatesStates(adnRouterHelper: AdnRouterHelperApi): Ng2StateDeclaration[] {
  const states = new StateDeclarationBuilder();
  const api = adnRouterHelper.services.service(ReportTemplateApiService);
  const permissions = adnRouterHelper.services.localUserPermissions();
  const userProfileSaver = adnRouterHelper.services.service(UserProfileSaverService);
  const readOnly = () => !permissions.hasNetwork(PERMISSIONS.manageReportTemplates.apiKey);
  const rh = adnRouterHelper.createRouterHelper('report-templates.report-template', ReportTemplateApiService, 'app.rep', 'app');
  const copyConfig = {
    modalComponent: ReportTemplateCopyModalComponent,
    stateUrl: 'app.rep.report-templates.report-template',
    lcase: 'report template',
    ucase: 'Report Template'
  };

  states.add(rh.listPath(), rh.list({
    resolve: {listReadOnly: readOnly},
    views: {
      'content@app': {component: ReportTemplateRoutePageComponent, bindings: {readOnly: 'listReadOnly'}},
      'context@app': {component: ListDefaultActionsComponent, bindings: {readOnly: 'listReadOnly', newStateName: 'listNewStateName'}},
      'title@app': stateTitleView()
    }
  }));
  states.add(rh.newItemPath(), rh.newItem());
  states.add(rh.itemPath(), rh.item({
    resolve: {
      reportTemplatePage: function($stateParams: any) {
        if ($stateParams.isNew) {
          return new SignalAdminResourcePageState<ReportTemplateModel>(
            {id: $stateParams.id, objectState: 'ACTIVE', validationWarnings: []}, true, readOnly(), api, 'Report Template', 'name', copyConfig
          );
        }
        return firstValueFrom(api.get($stateParams.id).pipe(
          tap((model) => userProfileSaver.saveObj(model, 'adnLastViewed'))
        )).then((model) => new SignalAdminResourcePageState(model, false, readOnly(), api, 'Report Template', 'name', copyConfig));
      },
      model: function(reportTemplatePage: SignalAdminResourcePageState<ReportTemplateModel>) {
        return reportTemplatePage.model();
      }
    },
    views: {
      'content@app': {component: ReportTemplateEditPageComponent, bindings: {state: 'reportTemplatePage'}},
      'context@app': {component: SignalAdminResourceContextActionsComponent, bindings: {state: 'reportTemplatePage'}},
      'title@app': {component: SignalAdminResourceTitleComponent, bindings: {state: 'reportTemplatePage'}}
    }
  }));
  return states.build();
}
